import { setPadMenuBack } from '../game/gamepad.ts'

/**
 * The notice behind the menu's "Unofficial fan project" line: who this game does and does not
 * belong to, and credit for everything in it that someone else made.
 *
 * The Creative Commons models ask for exactly this: the work's title, its author, a link to it,
 * the licence, and a word that it was changed. Titles are the authors' own, as listed on Sketchfab.
 * A model added to the game gets a line here; the per-asset records stay in the
 * `*attribution.json` files under public/assets.
 */

const SKETCHFAB = 'https://sketchfab.com/3d-models/'
const CC_BY = 'https://creativecommons.org/licenses/by/4.0/'

/** [title as published, the model page's id on Sketchfab] */
type Model = [title: string, page: string]

interface ModelGroup {
  heading: string
  models: Model[]
}

/** All by Bornstellar Makes Eternallasting (jameslucino117), CC BY 4.0. */
const BORNSTELLAR: ModelGroup[] = [
  {
    heading: 'Maps',
    models: [
      ['Halo 1>Multiplayer>Xbox>Blood Gulch', 'halo-1multiplayerxboxblood-gulch-2e91087b575648738263824b945a37ec'],
      ['Halo 3>Multiplayer>Default>Epitaph', 'halo-3multiplayerdefaultepitaph-8093234c362c48d3ab3d040bdb55f2cf'],
      ['Halo 3>Multiplayer>Default>Snowbound', 'halo-3multiplayerdefaultsnowbound-c0ae74719dc24eedb338b5a6bbddce83'],
      ['Halo 3>Multiplayer>Heroic>Rat’s Nest', 'halo-3multiplayerheroicrats-nest-01b10738e66d4be6ae197822c429addf'],
      ['Halo 3>Multiplayer>Legendary>Avalanche', 'halo-3multiplayerlegendaryavalanche-1e65369b1c9b47f4818de6512ec7d954'],
      ['Halo 3>Multiplayer>Legendary>Cold Storage', 'halo-3multiplayerlegendarycold-storage-8a25a502d7844a5780e561a5305a5950'],
      ['Halo Online>Bridge (Narrows)', 'halo-onlinebridge-d5545ddd2d914f57961e542b7549710a'],
      ['Halo Online>Guardian', 'halo-onlineguardian-136fa3f29cf045999d7219b9957e0a61'],
      ['Halo Online>Hangar (The Pit)', 'halo-onlinehangar-45dec0cf10874328a6d0c0b29408854b'],
      ['Halo Online>Lockout', 'halo-onlinelockout-f8496f3f0a6942278f944b094ccb52af'],
      ['Halo Online>Riverworld (Valhalla)', 'halo-onlineriverworld-15fda20729644890b66eac01189eba31'],
      ['Halo Online>Sandtrap', 'halo-onlinesandtrap-58b672cb248042ec8b7a44736a8f6494'],
    ],
  },
  {
    heading: 'Weapons and equipment',
    models: [
      ['Weapons>Human>Assault Rifle', 'weaponshumanassault-rifle-f38170a1205e4f4782e95cde9a2a34fa'],
      ['Weapons>Human>Battle Rifle', 'weaponshumanbattle-rifle-75d5ac8195ad4e05b7eae1f376f7a1bf'],
      ['Weapons>Human>Flamethrower', 'weaponshumanflamethrower-b3de9a970f254f2b82bea0d1c665ba8a'],
      ['Weapons>Human>Fragmentation Grenade', 'weaponshumanfragmentation-grenade-b6c0e8c8cb644f369ef3325da5953155'],
      ['Weapons>Human>MMG', 'weaponshumanmmg-b9973b6a252348acb05c2898489ff797'],
      ['Weapons>Human>Pistol', 'weaponshumanpistol-f20be1c188fa4d799519c72799da5ea0'],
      ['Weapons>Human>Rocket Launcher', 'weaponshumanrocket-launcher-d7b9fb643b7c4114a4f914e781192e4f'],
      ['Weapons>Human>Shotgun', 'weaponshumanshotgun-217eeef6ddad4471882a115d2216d012'],
      ['Weapons>Human>SMG', 'weaponshumansmg-6942abb55fcb408fb6cf78ce12d9b120'],
      ['Weapons>Human>Sniper Rifle', 'weaponshumansniper-rifle-7a8f55e354894aa3b62385db37fad4a4'],
      ['Weapons>Human>Spartan Laser', 'weaponshumanspartan-laser-9350382242ce4486a840ae224e63a081'],
      ['Weapons>Covenant>Brute Shot', 'weaponscovenantbrute-shot-9f1e9be8f730475187e10ed03b1eb5f6'],
      ['Weapons>Covenant>Carbine', 'weaponscovenantcarbine-246e2ed2c8204daf9b0193a556eb4e8a'],
      ['Weapons>Covenant>Energy Sword', 'none-7d43e43795f84be69200082cb6a28a91'],
      ['Weapons>Covenant>Fuel Rod Gun', 'weaponscovenantfuel-rod-gun-175b0a6a4e62407cb1848001f1e237a9'],
      ['Weapons>Covenant>Gravity Hammer', 'none-245e0492454d471da9beb997f91f94b2'],
      ['Weapons>Covenant>Mauler', 'weaponscovenantmauler-e1de83a85eca4f7587907e8ff8e1bc58'],
      ['Weapons>Covenant>Needler', 'weaponscovenantneedler-9528155ca1cc45fea648e4eed9c8c9b8'],
      ['Weapons>Covenant>Plasma Pistol', 'weaponscovenantplasma-pistol-f27e226106cf4c98bcb595fb1f3a3d80'],
      ['Weapons>Covenant>Plasma Rifle', 'weaponscovenantplasma-rifle-6cff7b1305934438b511dfe604e5bcda'],
      ['Weapons>Covenant>Spiker', 'weaponscovenantspiker-c7117137811144a994dbec8e00369d75'],
      ['Pickups>Flags', 'pickupsflags-f24b4467fa2f44ebaa3adb68d564bd0a'],
      ['Pickups>Regenerator', 'pickupsregenerator-95b1cb258b9447949050953b82c05686'],
    ],
  },
  {
    heading: 'Vehicles',
    models: [
      ['Vehicles>Human>Ground>Warthogs>Halo 3', 'vehicleshumangroundwarthogshalo-3-6c160ba011634347b0e41f2ea93cd995'],
      ['Vehicles>Human>Ground>Mongoose', 'vehicleshumangroundmongoose-a3b2ef93aad24b1a8ccc6c0f58b59fe8'],
      ['Vehicles>Human>Ground>Scorpion', 'vehicleshumangroundscorpion-96da38d872cf4250b1830d7c0b63d6c0'],
      ['Vehicles>Covenant>Ground>Ghost', 'vehiclescovenantgroundghost-1c580ca67a8f4055b7c8c61b115024b8'],
      ['Vehicles>Covenant>Ground>Chopper', 'vehiclescovenantgroundchopper-8e369b9ba8464d40a9b0cbc3c896f920'],
      ['Vehicles>Covenant>Fighters>Banshee', 'vehiclescovenantfightersbanshee-2cd945e1e3614cfea7445c04f7e7a50d'],
      ['Vehicles>Covenant>Transports>Phantom', 'acdf8a11ddc5463595a329005c90b8b1'],
      ['Vehicles>Covenant>Transports>Dropship', '32997630596c490c85dfe718d6d47721'],
    ],
  },
  {
    heading: 'Characters',
    models: [
      ['Enemies>Halo 3>Covenant>Elites>Minor', '7690307789604d10bfc6d9d45d9c7466'],
      ['Enemies>Halo 3>Covenant>Elites>Major', '1305e62dbbf84dea9e4a2e7b2a6f5d21'],
      ['Enemies>Halo 3>Covenant>Elites>Ultra', '748c7e1f3a874c749ee9326dc9fae6aa'],
      ['Enemies>Halo 3>Covenant>Brutes', 'c33426dfbb9a4d949a601595487f7cb2'],
      ['Enemies>H2A>Covenant>Grunts', '2a95d4fd21ef41df8eecda5d538841ac'],
      ['Enemies>Halo 3>The Flood>Pure Tank', 'enemieshalo-3the-floodpure-tank-2eae68d5a0214d23b95af2d12d09e6e5'],
      ['Enemies>halo 3>The Flood>Infection', 'enemieshalo-3the-floodinfection-3a48adac40874b08910eb3f17bade3f3'],
      ['Characters>Forerunners>343 Guilty Spark', 'charactersforerunners343-guilty-spark-76d34334fdea4d869a11397edbad4c56'],
    ],
  },
]

/** Models by anyone else, each CC BY 4.0: [title, page, author, what it is here]. */
const OTHER_MODELS: [title: string, page: string, author: string, use: string][] = [
  ['Halo Infinite Master Chief Rigged', 'halo-infinite-master-chief-rigged-0ad7b1431a6748959d691e9c171c6e2c', 'bizarrefog1', 'the Spartan’s body and armour'],
  ['Master-chief (HALO: INFINITE)', 'master-chief-halo-infinite-ed2d44d5b38649c3be097f428b1246e8', 'John-117 (Master chief-117)', 'the Spartan’s gloves'],
  ['Halo Infinite Oddball', 'halo-infinite-oddball-b52582ce23f84c08b8e3d818a00d2c5a', 'Laxity', 'the oddball skull'],
]

const CSS = `
.crd-scrim{position:fixed;inset:0;z-index:1000;display:grid;place-items:center;padding:16px;box-sizing:border-box;
  background:rgba(2,6,12,.72);color:#bfe4ff;font:14px/1.5 ui-sans-serif,system-ui,sans-serif;text-align:left;touch-action:auto}
.crd-scrim[hidden]{display:none}
.crd-panel{width:min(560px,100%);max-height:calc(100dvh - 32px);display:flex;flex-direction:column;box-sizing:border-box;
  background:rgba(6,18,28,.96);border:1px solid #3f8f7d;border-radius:8px;box-shadow:0 18px 60px rgba(0,0,0,.55)}
.crd-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 16px 10px;
  border-bottom:1px solid rgba(63,143,125,.45)}
.crd-head h2{margin:0;font-size:15px;font-weight:600;letter-spacing:.24em;text-transform:uppercase;color:#d8f2ff}
.crd-close{width:36px;height:36px;border:0;border-radius:5px;background:none;color:#bfe4ff;font:inherit;font-size:22px;line-height:1;cursor:pointer}
.crd-close:hover{background:rgba(191,228,255,.08)}
.crd-body{overflow-y:auto;overscroll-behavior:contain;padding:14px 16px 16px;flex:1;min-height:0;-webkit-user-select:text;user-select:text}
.crd-body p{margin:0 0 10px}
.crd-notice{color:#d8f2ff}
.crd-body p.crd-after{margin-top:10px}
.crd-body h3{margin:18px 0 6px;padding-top:14px;border-top:1px solid rgba(191,228,255,.06);font-size:15px;font-weight:600;color:#d8f2ff}
.crd-body h4{margin:12px 0 2px;font-size:11px;font-weight:600;letter-spacing:.16em;text-transform:uppercase;color:#7fb8a8}
.crd-body ul{margin:0;padding-left:18px}
.crd-body li{margin:3px 0;overflow-wrap:anywhere}
.crd-body li::marker{color:#3f8f7d}
.crd-body a{color:#9fd8ef;text-underline-offset:3px}
.crd-body a:hover{color:#d8f2ff}
.crd-scrim :focus-visible{outline:2px solid #8fd3ff;outline-offset:2px}
@media (max-height:480px){.crd-head{padding-top:8px;padding-bottom:6px}}
`

const link = (href: string, text: string): string => `<a href="${href}" target="_blank" rel="noopener">${text}</a>`
const model = ([title, page]: Model): string => `<li>${link(SKETCHFAB + page, title.replaceAll('>', ' › '))}</li>`

const BODY = `
<p class="crd-notice">nothalo is a free, unofficial fan project. It is not affiliated with or endorsed by Microsoft, Xbox Game Studios or Halo Studios.</p>
<p>Halo and its names, characters, designs and sounds belong to Microsoft Corporation. The game’s code is © Caden Burleson.</p>

<h3>3D models</h3>
<p>By ${link('https://sketchfab.com/jameslucino117', 'Bornstellar Makes Eternallasting')} (jameslucino117) on Sketchfab, under ${link(CC_BY, 'CC BY 4.0')}:</p>
${BORNSTELLAR.map(group => `<h4>${group.heading}</h4><ul>${group.models.map(model).join('')}</ul>`).join('')}
<h4>Other authors</h4>
<ul>${OTHER_MODELS.map(([title, page, author, use]) => `<li>${link(SKETCHFAB + page, title)} by ${author}, ${link(CC_BY, 'CC BY 4.0')}: ${use}.</li>`).join('')}</ul>
<p class="crd-after">Every model was changed for the game: materials converted, textures resized, geometry simplified, and parts re-rigged, re-posed or combined.</p>

<h3>Animation, textures, sounds and type</h3>
<ul>
<li>Walk, run and flinch animations for the Covenant: ${link('https://www.mixamo.com', 'Adobe Mixamo')}, under Mixamo’s terms.</li>
<li>Bark, concrete and rock textures on Guardian: ${link('https://polyhaven.com', 'Poly Haven')}, ${link('https://polyhaven.com/license', 'CC0')}.</li>
<li>A few fallback sounds: ${link('https://kenney.nl', 'Kenney')}, ${link('https://creativecommons.org/publicdomain/zero/1.0/', 'CC0')}.</li>
<li>Barlow Semi Condensed: The Barlow Project Authors, ${link('https://openfontlicense.org', 'SIL Open Font License 1.1')}.</li>
<li>Weapon, footstep and voice sounds, and the medal announcer (performed by Jeff Steitzer), are recordings from the Halo games and belong to Microsoft. They are not under a Creative Commons licence.</li>
</ul>
`

let scrim: HTMLElement | null = null
let opener: HTMLElement | null = null

/** Wire the menu's notice line: pressing it opens the full notice and the credits. */
export function attachCredits(button: HTMLElement): void {
  button.addEventListener('click', () => openCredits(button))
}

export function openCredits(from?: HTMLElement): void {
  opener = from ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null)
  const root = build()
  root.hidden = false
  root.querySelector<HTMLElement>('.crd-body')!.scrollTop = 0
  setPadMenuBack(close)
  addEventListener('keydown', onKey, true)
  root.querySelector<HTMLElement>('.crd-close')!.focus({ preventScroll: true })
}

function close(): void {
  if (!scrim || scrim.hidden) return
  scrim.hidden = true
  setPadMenuBack(null)
  removeEventListener('keydown', onKey, true)
  opener?.focus({ preventScroll: true })
}

function onKey(event: KeyboardEvent): void {
  if (event.code !== 'Escape') return
  event.preventDefault()
  event.stopPropagation()
  close()
}

function build(): HTMLElement {
  if (scrim) return scrim
  const style = document.createElement('style')
  style.textContent = CSS
  document.head.append(style)
  scrim = document.createElement('div')
  scrim.className = 'crd-scrim'
  scrim.hidden = true
  scrim.setAttribute('role', 'dialog')
  scrim.setAttribute('aria-modal', 'true')
  scrim.setAttribute('aria-labelledby', 'crd-title')
  scrim.innerHTML = `<div class="crd-panel">
    <div class="crd-head"><h2 id="crd-title">About and credits</h2><button class="crd-close" type="button" aria-label="Close about and credits">×</button></div>
    <div class="crd-body" data-pad-scroll>${BODY}</div>
  </div>`
  scrim.querySelector('.crd-close')!.addEventListener('click', close)
  scrim.addEventListener('click', event => { if (event.target === scrim) close() })
  document.body.append(scrim)
  return scrim
}
